/**
 * status-message.js
 * @author: Bernhard Wolkerstorfer
 *
 * Description: Generic message container.
 *
 * Copyright (c) 2012 Amazon.com, Inc. All rights reserved.
 *
 */
import {getState} from "../state/state";
import {MetricsNames} from "../metrics/metrics";
import {addFocusLimitation, removeFocusLimitation} from "../../src-common/utils/s2k-focus-helper";

/**
 * @constructor
 */
class StatusMessage {
    constructor(mode) {
        this.stateWindow = $(getState().stateWindow);
        this.containerType = "default";
        this.contentType = mode || "result";

        // the injection state, if it is false, will inject when calling `show alert`. Will be re-set when terminate
        this.hasInjected = false;

        // Layout related
        this.containerSettings = undefined;
        this.contentSettings = undefined;
        this.container = undefined;

        // Feedback related
        this.feedbackText = "";
        this.rating = 0;

        // Event handlers
        this.handlers = {
            'cancelProgress': undefined,
            'submitFeedback': undefined,
        };

        // ######## Constants ########
        /**
         * CSS Style to prevent from overwritten by webpage
         */
        this.CSS_STYLE = `
            #s2k-alert-body-commit {
                font-color: #0F1111 !important;
                color: #0F1111 !important;
            }
            
            #s2k-alert-body-commit:hover {
                font-color: #0F1111 !important;
                color: #0F1111 !important;
            }
            
            #s2k-alert-body-cancel {
                font-color: #0F1111 !important;
                color: #0F1111 !important;
            }
            
            #s2k-alert-body-cancel:hover {
                font-color: #0F1111 !important;
                color: #0F1111 !important;
            }
            
            #s2k-alert-header-close {
                box-shadow: none !important;
                background-color: transparent !important;
            }
            
            #s2k-alert-header-close:hover {
                background-color: transparent !important;
            }
            
            #s2k-alert * {
                padding: 0;
                cursor: auto;
            }
            
            #s2k-alert:focus, #s2k-alert *:focus {
                outline: none !important;
            }
            
            #s2k-alert-header-text:focus {
                box-shadow: none !important;
                background-color: transparent !important;
            }
            
            #s2k-alert-body-text:focus {
                box-shadow: none !important;
                background-color: transparent !important;
            }
            
            #s2k-alert-header-close:focus {
                box-shadow: none !important;
                background-color: transparent !important;
            }
        `;

        /**
         * Message Container Types
         */
        this.CONTAINER = {
            /**
             * Standard message container for S2K.
             * Class 's2k-do-not-extract' is for identify this div need to be ignored when doing web content extracion. (Do no remove it!)
             */
            "default": {
                "layout": '<div id="s2k-alert" class="s2k-do-not-extract" role="group" tabindex="0"></div>',
                "styles": {
                    "#s2k-alert": {
                        "display": "none",
                        "position": "fixed",
                        "padding": "0",
                        "font-family": `Amazon Ember`,
                        "background": "#F0F2F2",
                        "width": "350px",
                        "top": "90px",
                        "z-index": 999999,
                        "box-shadow": "0px 4px 14px 0px #0F111180",
                        "text-align": "center",
                        "border-radius": "8px",
                        "overflow": "hidden"
                    }
                }
            }
        };

        /**
         * Message Modes
         */
        this.CONTENT = {
            /**
             * Alert for action results.
             */
            "result": {
                "width": 350,
                "layout":
                    '<div id="s2k-alert-header">' +
                    '   <span id="s2k-alert-header-text" tabindex="0"></span>' +
                    '   <button id="s2k-alert-header-close" s2k-aria="A11yIconClose"></button>' +
                    '</div>' +
                    '<div id="s2k-alert-body">' +
                    '   <span id="s2k-alert-body-text"></span>' +
                    '</div>' +
                    '<div id="s2k-alert-footer">' +
                    '   <div role="button" id="s2k-alert-body-commit" class="s2k-popup-button" tabindex="0"></div>' +
                    '   <div role="button" id="s2k-alert-body-cancel" class="s2k-popup-button" tabindex="0"></div>' +
                    '</div>',
                "styles": {
                    "#s2k-alert-header": {
                        "display": "flex",
                        "margin": "0",
                        "padding": "16px 24px 16px 24px",
                        "background": "#F0F2F2",
                        "border-bottom": "1px solid #D5D9D9"
                    },
                    "#s2k-alert-header-text": {
                        "margin": "auto auto auto 0",
                        "font-family": `Amazon Ember`,
                        "font-weight": "700",
                        "font-size": "16px",
                        "line-height": "24px",
                        "align-items": "center",
                        "text-align": "left",
                        "color": "#0F1111"
                    },
                    "#s2k-alert-header-close": {
                        "cursor": "pointer",
                        "margin": "auto 0 auto auto",
                        "min-width": "0",
                        "min-height": "0",
                        "padding": "2px",
                        "width": "15px",
                        "border": "none",
                        "height": "15px",
                        "background-repeat": "no-repeat",
                        "background-position": "center",
                        "background-image": `url("_EXTURL_/images/s2k-alert-close.svg")`,
                        "background-size": "11px 11px"
                    },
                    "#s2k-alert-body": {
                        "display": "flex",
                        "margin": "0",
                        "padding": "15px 24px 26px 24px",
                        "background": "#FFFFFF",
                    },
                    "#s2k-alert-body-text": {
                        "margin": "auto",
                        "width": "100%",
                        "font-family": `Amazon Ember`,
                        "font-weight": "400",
                        "font-size": "14px",
                        "line-height": "20px",
                        "align-items": "center",
                        "text-align": "left",
                        "color": "#0F1111"
                    },
                    ".s2k-popup-button": {
                        "cursor": "pointer",
                        "min-width": "0",
                        "min-height": "0",
                        "font-family": `Amazon Ember`,
                        "border-radius": "8px",
                        "box-shadow": "0px 2px 5px rgba(213, 217, 217, 0.5)",
                        "box-sizing": "border-box",
                        "padding": "6px 20px 6px 20px",
                        "font-weight": "400",
                        "font-size": "13px",
                        "line-height": "20px",
                        "align-items": "center",
                        "text-align": "center",
                        "color": "#0F1111"
                    },
                    "#s2k-alert-body-cancel": {
                        "min-width": "110px",
                        "border": "1px solid #D5D9D9",
                        "background": "#FFFFFF"
                    },
                    "#s2k-alert-body-commit": {
                        "min-width": "110px",
                        "border": "1px solid #FCD200",
                        "background": "#FFD814"
                    },
                    "#s2k-alert-footer": {
                        "display": "flex",
                        "flexWrap": "wrap",
                        "flex-direction": "row-reverse",
                        "justify-content": "flex-start",
                        "gap": "8px",
                        "margin": "0",
                        "padding": "0 24px 26px 24px",
                        "background": "#FFFFFF"
                    }
                },
                "title": {
                    "sending": chrome.i18n.getMessage("alertSendingTitle"),
                    "generalError": chrome.i18n.getMessage("alertSendingErrorTitle"),
                    "networkError": chrome.i18n.getMessage("alertSendingErrorTitle"),
                    "noAuthError": chrome.i18n.getMessage("alertSendingErrorTitle"),
                    "previewError": chrome.i18n.getMessage("alertPreviewErrorTitle"),
                    "extractionError": chrome.i18n.getMessage("alertPreviewErrorTitle"),
                    "lowQualityAlert": chrome.i18n.getMessage("alertLowQualityTitle"),
                },
                "messages": {
                    "sending": chrome.i18n.getMessage("alertSendingMessage"),
                    "generalError": chrome.i18n.getMessage("alertSendingGeneralErrorMessage"),
                    "networkError": chrome.i18n.getMessage("alertSendingNetworkErrorMessage"),
                    "noAuthError": chrome.i18n.getMessage("alertSendingNoAuthErrorMessage"),
                    "previewError": chrome.i18n.getMessage("alertPreviewGeneralErrorMessage"),
                    "extractionError": chrome.i18n.getMessage("alertPreviewExtractionErrorMessage"),
                    "lowQualityAlert": chrome.i18n.getMessage("alertLowQualityMessage"),
                },
                "commitButton": {
                    "sending": chrome.i18n.getMessage("alertButtonOK"),
                    "generalError": chrome.i18n.getMessage("alertButtonOK"),
                    "networkError": chrome.i18n.getMessage("alertButtonOK"),
                    "noAuthError": chrome.i18n.getMessage("alertButtonSignIn"),
                    "previewError": chrome.i18n.getMessage("alertButtonSendAnyway"),
                    "extractionError": chrome.i18n.getMessage("alertButtonOK"),
                    "lowQualityAlert": chrome.i18n.getMessage("alertButtonOK"),
                },
                "cancelButton": {
                    "sending": chrome.i18n.getMessage("alertButtonCancelSending"),
                    "generalError": chrome.i18n.getMessage("alertButtonCancel"),
                    "networkError": chrome.i18n.getMessage("alertButtonCancel"),
                    "noAuthError": chrome.i18n.getMessage("alertButtonCancel"),
                    "previewError": chrome.i18n.getMessage("alertButtonCancel"),
                    "extractionError": chrome.i18n.getMessage("alertButtonCancel"),
                    "lowQualityAlert": chrome.i18n.getMessage("alertButtonCancel"),
                },
            },

            /**
             * Alert for action processing.
             */
            "progress": {
                "width": 350,
                "layout":
                    '<div id="s2k-alert-process-container">' +
                    '   <div id="s2k-alert-spinner">' +
                    '   </div>' +
                    '   <div id="s2k-alert-body-text-container" tabindex="0">' +
                    '       <span id="s2k-alert-body-text" aria-hidden="true">' +
                    '       </span>' +
                    '   </div>' +
                    '   <div role="button" id="s2k-alert-body-cancel" class="s2k-popup-button" tabindex="0"></div>' +
                    '</div>',
                "styles": {
                    "#s2k-alert-process-container": {
                        "margin": "0",
                        "padding": "20px 15px 24px 15px",
                        "background": "#F0F2F2",
                    },
                    "#s2k-alert-spinner": {
                        "margin": "auto",
                        "width": "64px",
                        "height": "64px",
                        "animation": "fadeIn .3s ease-in forwards,rotate360 1s linear infinite",
                        "background-image": `url("_EXTURL_/images/s2k-spinner.svg")`
                    },
                    "#s2k-alert-body-text": {
                        "display": "block",
                        "margin": "12px auto auto 0",
                        "width": "100%",
                        "font-family": `Amazon Ember`,
                        "font-weight": "700",
                        "font-size": "16px",
                        "line-height": "24px",
                        "align-items": "center",
                        "text-align": "center",
                        "color": "#0F1111"
                    },
                    ".s2k-popup-button": {
                        "cursor": "pointer",
                        "font-family": `Amazon Ember`,
                        "min-width": "0",
                        "min-height": "0",
                        "border-radius": "8px",
                        "box-shadow": "0px 2px 5px rgba(213, 217, 217, 0.5)",
                        "box-sizing": "border-box",
                        "padding": "6px 20px 6px 20px",
                        "font-weight": "400",
                        "font-size": "13px",
                        "line-height": "20px",
                        "align-items": "center",
                        "text-align": "center",
                        "color": "#0F1111"
                    },
                    "#s2k-alert-body-cancel": {
                        "display": "inline-block",
                        "min-width": "110px",
                        "margin": "16px auto auto auto",
                        "border": "1px solid #D5D9D9",
                        "background": "#FFFFFF"
                    },
                },
                "messages": {
                    "sendingToKindle": chrome.i18n.getMessage("alertSendingToKindleMessage"),
                    "analyzing": chrome.i18n.getMessage("alertAnalyzingMessage"),
                    "loadingPreview": chrome.i18n.getMessage("alertLoadingPreviewMessage"),
                },
                "a11yMessages": {
                    "sendingToKindle": chrome.i18n.getMessage("A11yAlertSendingToKindleMessage"),
                    "analyzing": chrome.i18n.getMessage("A11yAlertAnalyzingMessage"),
                    "loadingPreview": chrome.i18n.getMessage("A11yAlertLoadingPreviewMessage"),
                },
                "cancelButton": {
                    "sendingToKindle": chrome.i18n.getMessage("alertButtonCancel"),
                    "analyzing": chrome.i18n.getMessage("alertButtonCancel"),
                    "loadingPreview": chrome.i18n.getMessage("alertButtonCancel"),
                },
            },

            /**
             * Alert for action feedback .
             */
            "feedback": {
                "width": 350,
                "layout":
                    '<div id="s2k-alert-header">' +
                    '   <span id="s2k-alert-header-text" tabindex="0"></span>' +
                    '   <button id="s2k-alert-header-close" s2k-aria="A11yIconClose"></button>' +
                    '</div>' +
                    '<div id="s2k-alert-body">' +
                    '   <span id="s2k-alert-body-text"></span>' +
                    '   <div id="s2k-alert-body-rate-container">' +
                    '       <button id="s2k-alert-body-rate-star-1" class="s2k-alert-body-rate-star"></button>' +
                    '       <button id="s2k-alert-body-rate-star-2" class="s2k-alert-body-rate-star"></button>' +
                    '       <button id="s2k-alert-body-rate-star-3" class="s2k-alert-body-rate-star"></button>' +
                    '       <button id="s2k-alert-body-rate-star-4" class="s2k-alert-body-rate-star"></button>' +
                    '       <button id="s2k-alert-body-rate-star-5" class="s2k-alert-body-rate-star"></button>' +
                    '   </div>' +
                    '   <textarea id="s2k-alert-body-feedback-textarea" maxlength="256" class="s2k-alert-feedback-textarea" placeholder=""></textarea>' +
                    '</div>' +
                    '<div id="s2k-alert-footer">' +
                    '   <button id="s2k-alert-body-commit" class="s2k-popup-button"></button>' +
                    '</div>',
                "styles": {
                    "#s2k-alert-header": {
                        "display": "flex",
                        "margin": "0",
                        "padding": "16px 24px 16px 24px",
                        "background": "#F0F2F2",
                        "border-bottom": "1px solid #D5D9D9"
                    },
                    "#s2k-alert-header-text": {
                        "margin": "auto auto auto 0",
                        "font-family": `Amazon Ember`,
                        "font-weight": "700",
                        "font-size": "16px",
                        "line-height": "24px",
                        "align-items": "center",
                        "text-align": "left",
                        "color": "#0F1111"
                    },
                    "#s2k-alert-header-close": {
                        "cursor": "pointer",
                        "margin": "auto 0 auto auto",
                        "padding": "2px",
                        "min-width": "0",
                        "min-height": "0",
                        "width": "15px",
                        "border": "none",
                        "height": "15px",
                        "background-image": `url("_EXTURL_/images/s2k-alert-close.svg")`,
                        "background-repeat": "no-repeat",
                        "background-position": "center",
                        "background-size": "11px 11px"
                    },
                    "#s2k-alert-body": {
                        "margin": "0",
                        "padding": "15px 24px 26px 24px",
                        "background": "#FFFFFF",
                    },
                    "#s2k-alert-body-text": {
                        "display": "block",
                        "margin": "0",
                        "width": "100%",
                        "font-family": `Amazon Ember`,
                        "font-weight": "400",
                        "font-size": "14px",
                        "line-height": "20px",
                        "align-items": "center",
                        "text-align": "left",
                        "color": "#0F1111"
                    },
                    "#s2k-alert-body-rate-container": {
                        "display": "flex",
                        "margin": "16px 0 0 0",
                        "gap": "0",
                        "padding": "0",
                        "background": "#FFFFFF",
                    },
                    ".s2k-alert-body-rate-star": {
                        "cursor": "pointer",
                        "border": "none",
                        "margin": "0",
                        "padding": "0",
                        "width": "19px",
                        "height": "18px",
                        "background-color": "transparent",
                        "background-image": `url("_EXTURL_/images/s2k-feedback-star-empty.svg")`
                    },
                    "#s2k-alert-body-feedback-textarea": {
                        "padding": "5px 7px 6px 7px",
                        "margin": "16px 0 0 0",
                        "height": "69px",
                        // need minus padding, border and shadow
                        "width": "calc(100% - 7px - 7px - 1px - 1px - 2px - 1px)",
                        "resize": "none",
                        "font-family": `Amazon Ember`,
                        "font-size": "14px",
                        "font-weight": "400",
                        "line-height": "20px",
                        "vertical-align": "top",
                        "text-align": "left",
                        "color": "#0F1111",
                        "text-overflow": "ellipsis",
                        "overflow": "hidden",
                        "outline": "none",
                    },
                    ".s2k-alert-feedback-textarea": {
                        "border-radius": "4px",
                        "border": "1px solid #888C8C",
                        "background": "#FFFFFF",
                        "box-shadow": "0px 2px 1px 0px rgba(15, 17, 17, 0.15) inset",
                    },
                    ".s2k-popup-button": {
                        "cursor": "pointer",
                        "font-family": `Amazon Ember`,
                        "border-radius": "8px",
                        "box-shadow": "0px 2px 5px rgba(213, 217, 217, 0.5)",
                        "box-sizing": "border-box",
                        "padding": "6px 20px 6px 20px",
                        "min-width": "0",
                        "min-height": "0",
                        "font-weight": "400",
                        "font-size": "14px",
                        "line-height": "20px",
                        "align-items": "center",
                        "text-align": "center",
                        "color": "#0F1111"
                    },
                    "#s2k-alert-body-commit": {
                        "min-width": "110px",
                        "margin": "auto 0 auto auto",
                        "border": "1px solid #FCD200",
                        "background": "#FFD814"
                    },
                    "#s2k-alert-footer": {
                        "display": "flex",
                        "margin": "0",
                        "padding": "0 24px 27px 24px",
                        "background": "#FFFFFF"
                    }
                },
                "title": {
                    "input": chrome.i18n.getMessage("alertFeedbackTitle"),
                    "finish": chrome.i18n.getMessage("alertFeedbackTitle"),
                },
                "messages": {
                    "input": chrome.i18n.getMessage("alertFeedbackInputMessage"),
                    "finish": chrome.i18n.getMessage("alertFeedbackFinishMessage"),
                },
                "commitButton": {
                    "input": chrome.i18n.getMessage("alertButtonSubmit"),
                    "finish": chrome.i18n.getMessage("alertButtonOK"),
                },
                "cancelButton": {
                    "input": chrome.i18n.getMessage("alertButtonSubmit"),
                    "finish": chrome.i18n.getMessage("alertButtonOK"),
                },
            },
        };
    }

    // ######## Methods ########
    /**
     * Inject progress information.
     */
    inject() {
        // Load state information.
        let body = $(this.stateWindow[0].document.body);

        // Create DOM element for progress information.
        if (this.CONTAINER[this.containerType] ) {
            // Set current container settings.
            this.containerSettings = this.CONTAINER[this.containerType];

            body.append(this.containerSettings.layout);

            // Inject CSS styles to prevent from impact by target webpage
            const styleElement = document.createElement('style');
            // Add styles into head of current page
            styleElement.textContent = this.CSS_STYLE;
            window.document.head.append(styleElement);

            // Register container in object.
            this.container = $("#s2k-alert");

            // Register event handler.
            this.stateWindow.on("resize.s2k-status", this.onResize.bind(this));

            // Ensure the container is hide
            this.container.hide();
        }

        // Set the injection state
        this.hasInjected = true;
    }

    /**
     * Switch the status message mode.
     * @param {"result" | "progress" | "feedback"} type Type of alert to show
     * @param {"sending" | "generalError" | "networkError" | "noAuthError" | "previewError" | "extractionError" | "lowQualityAlert" | "sendingToKindle" | "analyzing" | "loadingPreview" | "input" | "finish" | undefined} content Content of alert to show
     */
    showAlert(type, content) {
        if (!this.hasInjected) {
            this.inject();
        }
        // Fade out container.
        this.container.hide();

        // Update message mode.
        this.contentType = type;

        if (this.CONTENT[this.contentType] ) {
            // Load settings for selected type.
            this.contentSettings = this.CONTENT[this.contentType];

            // Clear container and append layout of current alert.
            this.container.empty().append(this.contentSettings.layout);

            // Format message container with default style.
            this.format(this.container, this.containerSettings.styles);

            // Format message container with mode layout.
            this.format(this.container, this.contentSettings.styles);

            // A11y Focus (find header text first, if not have, change to body text)
            let titleDiv = this.container.find("#s2k-alert-header-text");

            // Config content based on alert type
            switch (type) {
                case "result":
                    this.loadResultAlert(content);
                    break;
                case "feedback":
                    this.loadFeedbackAlert(content);
                    break;
                case "progress":
                    // Progress alert not have header, change A11y focus to body
                    titleDiv = this.container.find("#s2k-alert-body-text-container");
                    this.loadProcessAlert(content);
                    break;
            }

            // Set A11y string
            this.container.attr("aria-label", chrome.i18n.getMessage("A11yAlert"));
            this.container.find("[s2k-aria]").each(function() {
                const $element = $(this);
                const ariaMessage = chrome.i18n.getMessage($element.attr("s2k-aria"));
                $element.attr("aria-label", ariaMessage);
            });

            // Set close action
            let closeButton = this.container.find("#s2k-alert-header-close");
            closeButton.on("click", function () {
                this.terminate(true);
            }.bind(this));

            // Resize status message and show it.
            this.onResize();
            this.container.show();
            // Reset teh focus limitation
            removeFocusLimitation("s2k-alert");
            addFocusLimitation("s2k-alert");
            // Set A11y focus
            titleDiv.focus();
        }
    }

    /**
     * Format a node with the provided styles.
     * @param $node     Node
     * @param styles    Hash(Selector, Style)
     */
    format($node, styles) {
        for (let selector in styles) {
            // Load the CSS style.
            let cssStyle = styles[selector];

            // Config Extension Url
            if (cssStyle["background-image"] ) {
                cssStyle["background-image"] = this.replaceUrlPlaceholder(cssStyle["background-image"]);
            }

            // Format matching elements with the mapped style.
            if ($node.is(selector)) {
                $node.css(cssStyle);
            } else {
                $node.find(selector).css(cssStyle);
            }
        }
    }

    /**
     * Replace placeholder to extension id
     * @param {string} oriString The string need to be updating placeholder
     * @return {string} The string replaced `_EXTURL_` to extension url
     */
    replaceUrlPlaceholder(oriString) {
        // If the extensionUrl is null means it is extension's local page, no need to add extension url.
        let extensionUrl = !getState().extensionUrl ? "" : getState().extensionUrl;
        return oriString.replace("_EXTURL_", extensionUrl);
    }

    /**
     * Config layout of result alert
     * @param {"sending" | "generalError" | "networkError" | "noAuthError" | "previewError" | "extractionError" | "lowQualityAlert"} content Content of alert to show
     */
    loadResultAlert(content) {
        // Get elements
        let titleDiv = this.container.find("#s2k-alert-header-text");
        let messageDiv = this.container.find("#s2k-alert-body-text");
        let commitButton = this.container.find("#s2k-alert-body-commit");
        let cancelButton = this.container.find("#s2k-alert-body-cancel");

        // Since not every alert has 2 buttons, will use 2 switch to control it
        let hasCancelButton = true;
        let hasCommitButton = true;

        // Set text to title and message
        titleDiv.text(this.contentSettings.title[content]);
        messageDiv.text(this.contentSettings.messages[content]);
        commitButton.text(this.contentSettings.commitButton[content]);
        cancelButton.text(this.contentSettings.cancelButton[content]);

        // Pre define close function because it will be use frequently later
        let closeFunc = function () {
            this.terminate(true);
        }.bind(this);

        // Config buttons
        switch (content) {
            case "previewError":
                commitButton.on("click", function () {
                    // Need to callback to continue trigger send workflow, but we do not have this error case for now.
                    this.terminate(true);
                }.bind(this));
                cancelButton.on("click", closeFunc);
                break;
            case "noAuthError":
                // Sign in button
                commitButton.on("click", function () {
                    // Need to open sign in page (For MP, use the cache from last time, and use default US if there's no cache.)
                    chrome.runtime.sendMessage({"action": "sign-in"}, function () {
                        // Close alert
                        this.terminate(true);
                    }.bind(this));
                }.bind(this));
                // Cancel button
                cancelButton.on("click", closeFunc);
                break;
            case "sending":
            case "generalError":
            case "networkError":
            case "extractionError":
            case "lowQualityAlert":
                hasCancelButton = false;
                commitButton.on("click", closeFunc);
                break;
        }

        // Layout buttons based on visibility
        if (!hasCancelButton) {
            cancelButton.hide();
        }
        if (!hasCommitButton) {
            commitButton.hide();
        }
    }

    /**
     * Config layout of progress alert
     * @param {"sendingToKindle" | "analyzing" | "loadingPreview"} content Content of alert to show
     */
    loadProcessAlert(content) {
        // Get element of title and message
        let messageDiv = this.container.find("#s2k-alert-body-text");
        let messageContainerDiv = this.container.find("#s2k-alert-body-text-container");
        let cancelButton = this.container.find("#s2k-alert-body-cancel");

        // Set text to process message
        messageDiv.text(this.contentSettings.messages[content]);
        messageContainerDiv.attr("aria-label", this.contentSettings.a11yMessages[content]);
        cancelButton.text(this.contentSettings.cancelButton[content]);

        // Config actions
        cancelButton.on("click", function () {
            if (this.handlers.cancelProgress) {
                this.handlers.cancelProgress();
            }
            this.terminate(true);
        }.bind(this));
    }

    /**
     * Config layout of progress alert
     * @param {"input" | "finish"} content Content of alert to show
     */
    loadFeedbackAlert(content) {
        // Get element of title and message
        let titleDiv = this.container.find("#s2k-alert-header-text");
        let messageDiv = this.container.find("#s2k-alert-body-text");
        let rateContainer = this.container.find("#s2k-alert-body-rate-container");
        let textArea = this.container.find('#s2k-alert-body-feedback-textarea');
        let commitButton = this.container.find("#s2k-alert-body-commit");
        let cancelButton = this.container.find("#s2k-alert-body-cancel");

        // Set placeholder of textarea
        textArea.attr('placeholder', chrome.i18n.getMessage("popUpFeedbackPlaceholder"));

        // Set text to title and message
        titleDiv.text(this.contentSettings.title[content]);
        messageDiv.text(this.contentSettings.messages[content]);
        commitButton.text(this.contentSettings.commitButton[content]);
        cancelButton.text(this.contentSettings.cancelButton[content]);

        // Decide which button to show based on content type
        if (content === "input") {
            this.loadStars();
            cancelButton.hide();
            commitButton.on("click", function () {
                if (this.handlers.submitFeedback) {
                    this.handlers.submitFeedback({text: textArea.val(), rating: this.rating});
                }
            }.bind(this));
        } else {
            rateContainer.hide();
            textArea.hide();
            commitButton.on("click", function () {
                this.terminate(true);
            }.bind(this));
        }
    }

    /**
     * Load click action of rating stars in feedback alert
     */
    loadStars() {
        let prefix = "s2k-alert-body-rate-star-";
        // Loop to add click action to each star
        for (let index = 1; index <= 5; index++) {
            let star = $('#' + prefix + index);
            this.setA11yofStars(0);
            star.on("click", function () {
                //re-set A11y suffix
                this.setA11yofStars(index);
                // Record rating
                this.rating = index;
                // Change UI of stars
                this.onStarClick(index);
            }.bind(this));
        }
    }

    /**
     * Load click action of rating stars in feedback alert
     * @param selectedIndex The index of selected star
     */
    setA11yofStars(selectedIndex) {
        let prefix = "s2k-alert-body-rate-star-";
        // Loop to add click action to each star
        for (let index = 1; index <= 5; index++) {
            let star = $('#' + prefix + index);
            let starNameA11y = chrome.i18n.getMessage("A11yFeedbackRating") + index + chrome.i18n.getMessage("A11yFeedbackStar");
            let starSuffixA11y = chrome.i18n.getMessage("A11yFeedbackCurrentRating") + selectedIndex + chrome.i18n.getMessage("A11yFeedbackStar");
            star.attr("aria-label", starNameA11y + ", " + starSuffixA11y);
        }
    }

    /**
     * Handle click action of rating stars in feedback page
     * @param {int} index The index of clicked star
     */
    onStarClick(index) {
        let metricsName = MetricsNames["preview-feedback-star-click"] + index;
        chrome.runtime.sendMessage({
            action: "metrics-add-counter",
            data: {
                metricsName: metricsName
            }
        });
        let prefix = "s2k-alert-body-rate-star-";
        // Loop to add click action to each star
        for (let i = 1; i <= 5; i++) {
            let star = $('#' + prefix + i);
            if (i <= index) {
                star.css('background-image', star.css('background-image').replace("empty", "filled"));
            } else {
                star.css('background-image', star.css('background-image').replace("filled", "empty"));
            }
        }
    }

    /**
     * Remove status message container from DOM.
     * @param {boolean} immediately whether close the alert immediately, it will has 1.5 seconds delay in default
     */
    terminate(immediately) {
        setTimeout(function () {
            this.container.fadeOut("fast", function () {
                this.container.remove();
                removeFocusLimitation("s2k-alert");
                // Re-set the injection state
                this.hasInjected = false;
            }.bind(this));
        }.bind(this), immediately ? 0 : 1500);

        this.stateWindow.off("resize.s2k-status");
    }

    // ################################ Event Handler ################################
    /**
     * Event handler for resize events.
     */
    onResize() {
        // Calculate left position for progress information.
        this.container.css("left", ((this.stateWindow.width() - this.contentSettings.width) / 2) + "px");
    }

    // ################################ Delegate ################################
    /**
     * Register the callback to monitoring event of user interaction
     * @param {keyof StatusMessage['handlers']} event
     * @param {(...args: any) => void} handler
     */
    registerEvent(event, handler) {
        this.handlers[event] = handler;
    }
}

export {StatusMessage};
